import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2, Plus, ShieldCheck, ShieldAlert, Workflow, Users, Copy, GripVertical } from "lucide-react";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import CompanyAISettingsCard from "@/components/CompanyAISettingsCard";

function PipelineStagesCard() {
    const [stages, setStages] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/company/pipeline-stages");
            setStages(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= stages.length) return;
        const copy = [...stages];
        [copy[i], copy[j]] = [copy[j], copy[i]];
        setStages(copy);
    };
    const update = (i, k, v) => {
        const copy = [...stages];
        copy[i] = { ...copy[i], [k]: v };
        if (k === "label") copy[i].key = (v || "").trim();
        setStages(copy);
    };
    const remove = (i) => {
        if (stages.length <= 2) return toast.error("Keep at least 2 stages");
        setStages(stages.filter((_, idx) => idx !== i));
    };
    const add = () => setStages([...stages, { key: `Stage ${stages.length + 1}`, label: `Stage ${stages.length + 1}`, color: "#64748B" }]);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/company/pipeline-stages", { stages });
            toast.success("Pipeline stages saved");
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    return (
        <section className="border border-border bg-white p-8 space-y-4">
            <div className="flex items-center gap-2">
                <Workflow className="w-5 h-5 text-ikb" />
                <h2 className="font-display font-bold text-xl">Pipeline stages</h2>
            </div>
            <p className="text-sm text-muted-foreground -mt-2">Customize the stages every applicant moves through (Applied → Hired). Reflected on every Job view.</p>
            <div className="space-y-2">
                {stages.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 border border-border bg-white p-2" data-testid={`stage-row-${i}`}>
                        <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
                        <input type="color" value={s.color || "#64748B"} onChange={(e) => update(i, "color", e.target.value)} className="w-8 h-8 cursor-pointer" />
                        <Input value={s.label} onChange={(e) => update(i, "label", e.target.value)} className="rounded-none h-9 flex-1" data-testid={`stage-label-${i}`} />
                        <Button type="button" variant="outline" className="rounded-none h-9 px-2" onClick={() => move(i, -1)} disabled={i === 0}>↑</Button>
                        <Button type="button" variant="outline" className="rounded-none h-9 px-2" onClick={() => move(i, 1)} disabled={i === stages.length - 1}>↓</Button>
                        <button type="button" onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`stage-remove-${i}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-between">
                <Button type="button" variant="outline" className="rounded-none" onClick={add} data-testid="stage-add"><Plus className="w-4 h-4 mr-1" /> Add stage</Button>
                <Button onClick={save} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="stages-save">{busy ? "Saving..." : "Save pipeline"}</Button>
            </div>
        </section>
    );
}

function TalentPoolCard({ company, onSaved }) {
    const [form, setForm] = useState({
        talent_pool_enabled: !!company?.talent_pool_enabled,
        talent_pool_slug: company?.talent_pool_slug || "",
        talent_pool_intro: company?.talent_pool_intro || "",
        talent_pool_list_id: company?.talent_pool_list_id || "",
    });
    const [lists, setLists] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/hiring-lists").then((r) => setLists(r.data || [])).catch(() => {});
    }, []);

    const slug = (form.talent_pool_slug || "").toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-");
    const url = slug ? `${window.location.origin}/p/${slug}` : "";

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.patch("/company/me", { ...form, talent_pool_slug: slug });
            toast.success("Talent pool saved");
            onSaved && onSaved();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    const copyUrl = async () => {
        if (!url) return;
        try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error(url); }
    };

    return (
        <form onSubmit={save} className="border border-border bg-white p-8 space-y-5">
            <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-ikb" />
                <h2 className="font-display font-bold text-xl">Public talent pool</h2>
            </div>
            <p className="text-sm text-muted-foreground -mt-2">Capture evergreen profiles even when you have no open roles. Embed the link on your careers page — every visitor becomes a candidate in your DB.</p>
            <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.talent_pool_enabled} onCheckedChange={(v) => setForm({ ...form, talent_pool_enabled: !!v })} className="rounded-none" data-testid="tp-enabled" />
                <span className="text-sm font-medium">Enable public talent pool</span>
            </label>
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs uppercase tracking-widest font-bold">Public URL slug</Label>
                    <Input value={form.talent_pool_slug} onChange={(e) => setForm({ ...form, talent_pool_slug: e.target.value })} placeholder="acme-talent" className="rounded-none mt-2 h-11 font-mono" data-testid="tp-slug" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest font-bold">Auto-add to list (optional)</Label>
                    <select value={form.talent_pool_list_id || ""} onChange={(e) => setForm({ ...form, talent_pool_list_id: e.target.value || "" })} className="mt-2 h-11 w-full px-3 border border-border bg-white text-sm" data-testid="tp-list">
                        <option value="">— none —</option>
                        {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                </div>
                <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-widest font-bold">Intro shown to visitors</Label>
                    <Textarea rows={3} value={form.talent_pool_intro} onChange={(e) => setForm({ ...form, talent_pool_intro: e.target.value })} placeholder="We're not actively hiring, but we'd love to keep in touch..." className="rounded-none mt-2" data-testid="tp-intro" />
                </div>
            </div>
            {url && form.talent_pool_enabled && (
                <div className="flex items-center gap-2 text-xs font-mono border border-dashed border-border bg-secondary p-3">
                    <span className="text-muted-foreground">Share:</span>
                    <span className="font-medium flex-1 truncate">{url}</span>
                    <Button type="button" variant="outline" size="sm" onClick={copyUrl} className="rounded-none h-7" data-testid="tp-copy"><Copy className="w-3 h-3" /></Button>
                    <a href={url} target="_blank" rel="noreferrer" className="text-ikb hover:underline">Open</a>
                </div>
            )}
            <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="tp-save">{busy ? "Saving..." : "Save talent pool"}</Button>
        </form>
    );
}



function EmbedCard({ company }) {
    const slug = company?.company_slug;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const scriptSrc = `${origin}/api/public/embed/portalwiz.js`;
    const iframeSrc = slug ? `${origin}/embed/board/${slug}` : "";
    const scriptSnippet = slug
        ? `<script src="${scriptSrc}" data-portalwiz-company="${slug}" data-portalwiz-theme="light"></script>`
        : "";
    const iframeSnippet = slug
        ? `<iframe src="${iframeSrc}" width="100%" height="780" id="portalwiz-embed" frameborder="0" loading="lazy" title="Open roles" scrolling="no" style="border:0;display:block;transition:height .2s ease"></iframe>
<script>(function(){window.addEventListener("message",function(e){var d=e.data;if(!d)return;if(d.kind==="portalwiz:resize"||d.type==="portalwiz-resize"){var h=Number(d.height);if(h>=120&&h<=8000){var f=document.getElementById("portalwiz-embed");if(f)f.style.height=h+"px";}}});})();</script>`
        : "";

    const copy = async (text, label) => {
        try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
        catch { toast.error("Couldn't copy — select the text manually"); }
    };

    if (!slug) {
        return (
            <section className="border border-border bg-white p-8 space-y-4">
                <h2 className="font-display font-bold text-xl">Embeddable job board</h2>
                <p className="text-sm text-muted-foreground">Slug will be generated on next save — try saving the company profile above.</p>
            </section>
        );
    }

    return (
        <section className="border border-border bg-white p-8 space-y-6" data-testid="embed-card">
            <div>
                <h2 className="font-display font-bold text-xl">Embed your jobs anywhere</h2>
                <p className="text-sm text-muted-foreground mt-2">
                    Paste one of these snippets on your careers page and your open roles will appear, live and branded.
                    Candidates click through directly to your public apply pages.
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-px bg-border">
                <div className="bg-white p-5 space-y-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">Recommended · auto-resizes</div>
                    <h3 className="font-display font-bold text-base">Drop-in &lt;script&gt; tag</h3>
                    <p className="text-xs text-muted-foreground">Best for marketing sites — auto-loads, auto-resizes, no iframe styling needed.</p>
                    <pre className="text-[11px] font-mono bg-secondary border border-border p-3 whitespace-pre-wrap break-all overflow-x-auto" data-testid="embed-script-snippet">{scriptSnippet}</pre>
                    <Button type="button" variant="outline" className="rounded-none h-9" onClick={() => copy(scriptSnippet, "Script snippet")} data-testid="embed-copy-script">
                        <Copy className="w-3.5 h-3.5 mr-2" /> Copy script
                    </Button>
                </div>
                <div className="bg-white p-5 space-y-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Manual / iframe</div>
                    <h3 className="font-display font-bold text-base">Plain &lt;iframe&gt;</h3>
                    <p className="text-xs text-muted-foreground">For platforms that block third-party scripts (Squarespace, Webflow).</p>
                    <div className="border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-[11px] text-amber-900" data-testid="embed-wordpress-note">
                        <strong>WordPress users:</strong> Use an HTML/Custom-HTML block to paste BOTH the &lt;iframe&gt; AND the &lt;script&gt; together — many editors strip inline scripts from regular paragraphs, which breaks the auto-resize and crops the form when conditional fields appear.
                    </div>
                    <pre className="text-[11px] font-mono bg-secondary border border-border p-3 whitespace-pre-wrap break-all overflow-x-auto" data-testid="embed-iframe-snippet">{iframeSnippet}</pre>
                    <Button type="button" variant="outline" className="rounded-none h-9" onClick={() => copy(iframeSnippet, "Iframe snippet")} data-testid="embed-copy-iframe">
                        <Copy className="w-3.5 h-3.5 mr-2" /> Copy iframe
                    </Button>
                </div>
            </div>

            <div className="flex items-center justify-between gap-2 border border-dashed border-border bg-secondary p-3 text-xs font-mono flex-wrap">
                <span className="text-muted-foreground shrink-0">Live preview:</span>
                <a href={iframeSrc} target="_blank" rel="noreferrer" className="text-ikb hover:underline truncate min-w-0 break-all" data-testid="embed-preview-link">
                    {iframeSrc}
                </a>
            </div>

            <BrandingClickStats />
        </section>
    );
}

function BrandingClickStats() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/embed/branding-stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);
    if (!stats) return null;
    return (
        <div className="border border-border bg-white p-4" data-testid="embed-branding-stats">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h4 className="font-display font-bold text-sm uppercase tracking-widest">"Powered by Portalwiz" click stats</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">Tracks visitors who clicked the credit link below the embedded board.</p>
                </div>
                <div className="flex gap-2">
                    <div className="border border-border bg-secondary px-3 py-1.5 text-center min-w-[80px]">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">All-time</div>
                        <div className="text-lg font-display font-black">{stats.total}</div>
                    </div>
                    <div className="border border-border bg-secondary px-3 py-1.5 text-center min-w-[80px]">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Last 30 d</div>
                        <div className="text-lg font-display font-black">{stats.last_30d}</div>
                    </div>
                </div>
            </div>
            {(stats.recent || []).length > 0 && (
                <details className="mt-3">
                    <summary className="text-xs font-mono uppercase tracking-widest text-muted-foreground cursor-pointer">Recent clicks ({stats.recent.length})</summary>
                    <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                        {stats.recent.map((r) => (
                            <li key={r.id} className="text-[11px] font-mono text-muted-foreground border-l-2 border-border pl-2">
                                {new Date(r.ts).toLocaleString()} {r.referer ? `· from ${r.referer.replace(/^https?:\/\//, "").slice(0, 60)}` : ""}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}


export default function CompanyPage() {
    const { user } = useAuth();
    const [company, setCompany] = useState(null);
    const [recruiters, setRecruiters] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [open, setOpen] = useState(false);

    const load = async () => {
        const [c, r] = await Promise.all([api.get("/company/me"), api.get("/company/recruiters")]);
        setCompany(c.data || null);
        setRecruiters(r.data || []);
    };

    useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, []);

    const saveCompany = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.patch("/company/me", company);
            toast.success("Company updated");
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    const addRecruiter = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/company/recruiters", form);
            toast.success("Recruiter added");
            setForm({ name: "", email: "", password: "" });
            setOpen(false);
            await load();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    const removeRecruiter = async (id) => {
        if (!window.confirm("Remove this recruiter?")) return;
        try {
            await api.delete(`/company/recruiters/${id}`);
            toast.success("Removed");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (!company) return <div className="p-8 font-mono text-sm text-muted-foreground">Loading...</div>;

    const isOwner = user?.role === "employer";

    return (
        <div className="max-w-[1600px] mx-auto p-3 sm:p-5 lg:p-6 space-y-12">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Workspace</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">{company.name}</h1>
                <div className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-2 py-1 border border-border bg-white">
                    {company.kyc_status === "approved" ? (
                        <><ShieldCheck className="w-3 h-3 text-emerald-600" /> KYC approved</>
                    ) : (
                        <><ShieldAlert className="w-3 h-3 text-amber-600" /> KYC {company.kyc_status}</>
                    )}
                </div>
            </div>

            <CompanyAISettingsCard />

            {isOwner && (
                <form onSubmit={saveCompany} className="border border-border bg-white p-8 space-y-5">
                    <h2 className="font-display font-bold text-xl">Company profile</h2>
                    <div className="grid md:grid-cols-2 gap-5">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Company name</Label>
                            <Input value={company.name || ""} onChange={(e) => setCompany({ ...company, name: e.target.value })} className="rounded-none mt-2 h-11" data-testid="company-name" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Website</Label>
                            <Input value={company.website || ""} onChange={(e) => setCompany({ ...company, website: e.target.value })} className="rounded-none mt-2 h-11" placeholder="https://..." data-testid="company-website" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Industry</Label>
                            <Input value={company.industry || ""} onChange={(e) => setCompany({ ...company, industry: e.target.value })} className="rounded-none mt-2 h-11" data-testid="company-industry" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">Size</Label>
                            <Input value={company.size || ""} onChange={(e) => setCompany({ ...company, size: e.target.value })} className="rounded-none mt-2 h-11" placeholder="1-10, 11-50, 50-200..." data-testid="company-size" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs uppercase tracking-widest font-bold">Headquarters</Label>
                            <Input value={company.headquarters || ""} onChange={(e) => setCompany({ ...company, headquarters: e.target.value })} className="rounded-none mt-2 h-11" data-testid="company-hq" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs uppercase tracking-widest font-bold">About</Label>
                            <Textarea rows={4} value={company.description || ""} onChange={(e) => setCompany({ ...company, description: e.target.value })} className="rounded-none mt-2" data-testid="company-about" />
                        </div>
                    </div>
                    <div className="pt-2">
                        <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="company-save">{busy ? "Saving..." : "Save changes"}</Button>
                    </div>
                </form>
            )}

            {isOwner && <PipelineStagesCard />}
            {isOwner && <EmbedCard company={company} />}
            {isOwner && <TalentPoolCard company={company} onSaved={load} />}

            <section>
                <div className="flex items-end justify-between mb-4">
                    <h2 className="font-display font-bold text-2xl">Team</h2>
                    {isOwner && (
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="add-recruiter-btn"><Plus className="w-4 h-4 mr-2" /> Add recruiter</Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-none">
                                <DialogHeader>
                                    <DialogTitle className="font-display font-bold text-2xl">Add recruiter</DialogTitle>
                                </DialogHeader>
                                <form onSubmit={addRecruiter} className="space-y-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest font-bold">Name</Label>
                                        <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-2 h-11" data-testid="recruiter-name" />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest font-bold">Email</Label>
                                        <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-2 h-11" data-testid="recruiter-email" />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest font-bold">Initial password</Label>
                                        <Input type="password" minLength={6} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none mt-2 h-11" data-testid="recruiter-password" />
                                    </div>
                                    <DialogFooter>
                                        <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="recruiter-submit">{busy ? "Adding..." : "Add to team"}</Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
                <div className="border border-border bg-white divide-y divide-border">
                    {recruiters.length === 0 ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">No team members yet</div>
                    ) : recruiters.map((r) => (
                        <div key={r.id} className="flex items-center justify-between p-4">
                            <div>
                                <div className="font-medium">{r.name}</div>
                                <div className="text-xs text-muted-foreground font-mono">{r.email}</div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${r.role === "employer" ? "bg-ikb text-white" : "border border-border bg-white"}`}>
                                    {r.role === "employer" ? "Owner" : r.role}
                                </span>
                                {isOwner && r.role !== "employer" && (
                                    <>
                                        <button
                                            onClick={async () => {
                                                const pwd = window.prompt(`Set a new password for ${r.name}:`);
                                                if (!pwd) return;
                                                if (pwd.length < 6) { toast.error("Min 6 characters"); return; }
                                                try {
                                                    await api.post(`/company/recruiters/${r.id}/reset-password`, { new_password: pwd });
                                                    toast.success(`Password updated for ${r.name}. Share securely.`);
                                                } catch (err) { toast.error(formatApiError(err)); }
                                            }}
                                            className="text-xs font-mono uppercase tracking-widest border border-border px-2 py-1 hover:bg-secondary"
                                            data-testid={`reset-pwd-${r.id}`}
                                            title="Reset this recruiter's password"
                                        >
                                            Reset password
                                        </button>
                                        <button onClick={() => removeRecruiter(r.id)} className="text-destructive hover:text-destructive/80" data-testid={`remove-${r.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {isOwner && <InterviewersCard />}
        </div>
    );
}

function InterviewersCard() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/company/interviewers");
            setItems(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const add = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/company/interviewers", form);
            toast.success(`${form.name} invited as interviewer`);
            setForm({ name: "", email: "", password: "" });
            setOpen(false);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <section>
            <div className="flex items-end justify-between mb-4">
                <div>
                    <h2 className="font-display font-bold text-2xl">Interviewers</h2>
                    <p className="text-xs text-muted-foreground font-mono mt-1">
                        Read-only access to assigned candidates. Can add notes, ratings, interview feedback & Q&amp;A — but never see compensation.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="add-interviewer-btn">
                            <Plus className="w-4 h-4 mr-2" /> Add interviewer
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none">
                        <DialogHeader>
                            <DialogTitle className="font-display font-bold text-2xl">Add interviewer</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={add} className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Name</Label>
                                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-2 h-11" data-testid="interviewer-name" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Email</Label>
                                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-2 h-11" data-testid="interviewer-email" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Initial password</Label>
                                <Input type="password" minLength={6} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none mt-2 h-11" data-testid="interviewer-password" />
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="interviewer-submit">{busy ? "Adding..." : "Add interviewer"}</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <div className="border border-border bg-white divide-y divide-border">
                {items.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground" data-testid="interviewers-empty">
                        No interviewers yet. Add one above so they can be assigned to interview rounds.
                    </div>
                ) : items.map((r) => (
                    <div key={r.id} className="flex items-center justify-between p-4" data-testid={`interviewer-row-${r.id}`}>
                        <div>
                            <div className="font-medium">{r.name}</div>
                            <div className="text-xs text-muted-foreground font-mono">{r.email}</div>
                        </div>
                        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-border bg-white">Interviewer</span>
                    </div>
                ))}
            </div>
        </section>
    );
}
